import { Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Redirect,
  Route,
} from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState } from '../store/auth';
import { PublicRoute } from '../components/PublicRoute';
import { PrivateRoute } from '../components/PrivateRoute';
import {
  privateRoutes,
  publicRoutes,
  verifiedOnlyRoutes,
  expiredSubscriptionRoutes,
  pendingActivationRoutes,
  activeSubscriptionRoutes,
  publicOpenRoutes,
} from './routes';
import { Box } from '@mui/system';
import { LinearProgress } from '@mui/material';
import { VerifiedRoute } from '../components/VerifiedRoute';
import { ExpiredRoute } from '../components/ExpiredRoute';
import { PendingActivationRoute } from '../components/PendingActivationRoute';
import { ActiveCompanyRoute } from '../components/ActiveCompanyRoute';
import { userState } from '../store/user';
import { PublicOpenRoute } from '../components/PublicOpenRoute';
import PlatformRedirect from '../components/PlatformRedirect';

// These four pages used to live under /settings before each got its own launcher tile.
// Old bookmarks and the payment gateway's server-configured return URL still point at
// the previous paths, so forward them — keeping any sub-path, query and hash intact.
const LEGACY_SETTINGS_PATHS: Record<string, string> = {
  '/settings/billing': '/billing',
  '/settings/logger': '/audit-logs',
  '/settings/team-members': '/team-members',
  '/settings/notification-management': '/notifications',
};

const Routes = () => {
  const [auth] = useRecoilState(authState);
  const user = useRecoilValue(userState);

  return (
    <Box
      sx={{
        overflow: 'auto',
      }}
    >
      <Router>
        <Suspense
          fallback={
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          }
        >
          <Switch>
            {publicOpenRoutes.map(({ Component, ...rest }, i) => (
              <PublicOpenRoute key={i} {...rest}>
                <Component />
              </PublicOpenRoute>
            ))}

            {publicRoutes.map(({ Component, ...rest }, i) => (
              <PublicRoute key={i} {...rest}>
                <Component />
              </PublicRoute>
            ))}

            {privateRoutes.map(({ Component, ...rest }, i) => (
              <PrivateRoute key={i} {...rest}>
                <Component />
              </PrivateRoute>
            ))}

            {/* Must precede the /settings route below, which matches non-exactly. */}
            {Object.entries(LEGACY_SETTINGS_PATHS).map(([from, to]) => (
              <Route
                key={from}
                path={from}
                render={({ location }) => (
                  <Redirect
                    to={{
                      ...location,
                      pathname: location.pathname.replace(from, to),
                    }}
                  />
                )}
              />
            ))}

            {verifiedOnlyRoutes.map(({ Component, roles, ...rest }, i) => (
              <VerifiedRoute
                key={i}
                {...rest}
                canAccess={
                  roles ? (roles.includes(user?.role) ? true : false) : true
                }
              >
                <Component />
              </VerifiedRoute>
            ))}

            {activeSubscriptionRoutes.map(
              ({ Component, roles, ...rest }, i) => (
                <ActiveCompanyRoute
                  key={i}
                  {...rest}
                  canAccess={
                    roles ? (roles.includes(user?.role) ? true : false) : true
                  }
                >
                  <Component />
                </ActiveCompanyRoute>
              ),
            )}

            {expiredSubscriptionRoutes.map(
              ({ Component, roles, ...rest }, i) => (
                <ExpiredRoute
                  key={i}
                  {...rest}
                  canAccess={
                    roles ? (roles.includes(user?.role) ? true : false) : true
                  }
                >
                  <Component />
                </ExpiredRoute>
              ),
            )}

            {pendingActivationRoutes.map(({ Component, roles, ...rest }, i) => (
              <PendingActivationRoute
                key={i}
                {...rest}
                canAccess={
                  roles ? (roles.includes(user?.role) ? true : false) : true
                }
              >
                <Component />
              </PendingActivationRoute>
            ))}

            {/*
              Catch-all, so this is where the root and any unknown path land. A signed-in
              visitor belongs on the platform's launcher, not on a portal module — the
              portal is somewhere you arrive from a tile, not somewhere you start.

              `Route` rather than `Redirect` for the signed-in half: the launcher is a
              separate app on another origin, which the router cannot reach.
            */}
            {auth.token ? (
              <Route path='/' component={PlatformRedirect} />
            ) : (
              <Redirect from='/' to='/auth/login' />
            )}
          </Switch>
        </Suspense>
      </Router>
    </Box>
  );
};

export default Routes;
